import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2 } from "lucide-react";
import { motion } from "framer-motion";

interface Exercise {
  id: string;
  exercise_name: string;
  equipment: string | null;
  sets: number | null;
  reps: string | null;
  springs: string | null;
  focus_area: string | null;
  difficulty: string;
  notes: string | null;
  display_order: number;
}

interface ExerciseListProps {
  exercises: Exercise[];
  onEdit?: (exercise: Exercise) => void;
  onDelete?: (exerciseId: string) => void;
  readonly?: boolean;
}

const focusAreaColors: Record<string, string> = {
  core: "bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300",
  legs: "bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300",
  arms: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300",
  back: "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300",
  stretch: "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300",
};

const difficultyColors: Record<string, string> = {
  beginner: "bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300",
  intermediate: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300",
  advanced: "bg-rose-100 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300",
};

export default function ExerciseList({
  exercises,
  onEdit,
  onDelete,
  readonly = false,
}: ExerciseListProps) {
  const sortedExercises = [...exercises].sort(
    (a, b) => a.display_order - b.display_order
  );

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="w-12">#</TableHead>
            <TableHead>Exercício</TableHead>
            <TableHead>Equipamento</TableHead>
            <TableHead>Séries × Reps</TableHead>
            <TableHead>Molas</TableHead>
            <TableHead>Foco</TableHead>
            <TableHead>Dificuldade</TableHead>
            {!readonly && <TableHead className="w-24">Ações</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedExercises.map((exercise, index) => (
            <motion.tr
              key={exercise.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className="hover:bg-muted/50 transition-colors"
            >
              <TableCell className="font-medium text-muted-foreground">
                {index + 1}
              </TableCell>
              <TableCell className="font-medium">{exercise.exercise_name}</TableCell>
              <TableCell>
                <Badge variant="outline" className="font-normal">
                  {exercise.equipment || "—"}
                </Badge>
              </TableCell>
              <TableCell>
                {exercise.sets && exercise.reps
                  ? `${exercise.sets} × ${exercise.reps}`
                  : exercise.reps || "—"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {exercise.springs || "—"}
              </TableCell>
              <TableCell>
                {exercise.focus_area && (
                  <Badge
                    variant="secondary"
                    className={focusAreaColors[exercise.focus_area] || ""}
                  >
                    {exercise.focus_area}
                  </Badge>
                )}
              </TableCell>
              <TableCell>
                <Badge
                  variant="secondary"
                  className={difficultyColors[exercise.difficulty] || ""}
                >
                  {exercise.difficulty === "beginner" && "Iniciante"}
                  {exercise.difficulty === "intermediate" && "Intermediário"}
                  {exercise.difficulty === "advanced" && "Avançado"}
                </Badge>
              </TableCell>
              {!readonly && (
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => onEdit?.(exercise)}
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => onDelete?.(exercise.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              )}
            </motion.tr>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
